import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { type ApiError, apiFetchJson } from '../../lib/apiFetch.js';

/**
 * The ServiceTitan schedule feed (integration P2).
 *
 * READ-ONLY. This whole feature reads; nothing here writes to ServiceTitan or to QELM. The server
 * does the five-way join (appointments → jobs → assignments → customers → locations) because the
 * SPA should not be issuing a fan-out of upstream calls, and because the window has to be
 * re-checked server-side — ServiceTitan silently ignores unknown query parameters, so a renamed
 * filter would return the entire tenant's history dressed up as one week.
 */

/** One row of the schedule. Mirrors the server's `ScheduleRow`. */
export interface ScheduleRow {
  appointmentId: number;
  appointmentNumber: string | null;
  start: string;
  end: string | null;
  arrivalWindowStart: string | null;
  arrivalWindowEnd: string | null;
  appointmentStatus: string | null;
  isConfirmed: boolean;
  specialInstructions: string | null;

  jobId: number;
  jobNumber: string | null;
  jobStatus: string | null;
  jobSummary: string | null;
  jobTotal: number | null;

  businessUnitId: number | null;
  businessUnitName: string | null;

  customerName: string | null;
  siteAddress: string | null;
  technicians: string[];

  /**
   * The QELM job already imported from this ServiceTitan job, or null.
   *
   * Resolved server-side in one indexed read for the whole window (migration 096), so the board can
   * link through instead of offering the button twice. It is a display hint only — the server checks
   * again on import, and the unique index is what actually prevents a duplicate.
   */
  importedJobId: number | null;
}

export interface ScheduleResponse {
  ok: boolean;
  from: string;
  to: string;
  rows: ScheduleRow[];
  /** True when the window held more appointments than one read returns. */
  truncated: boolean;
}

export function scheduleQueryKey(from: string, to: string): readonly unknown[] {
  return ['servicetitan', 'schedule', from, to] as const;
}

/**
 * `to` is EXCLUSIVE — it maps to ServiceTitan's `startsBefore`. A single day is from=D, to=D+1.
 *
 * No polling and no refetch on focus: this calls a third-party API on every load, and a page left
 * open on a monitor should not sit there consuming somebody else's rate limit. Refresh is manual.
 */
export function useScheduleQuery(from: string, to: string, enabled = true) {
  return useQuery<ScheduleResponse>({
    queryKey: scheduleQueryKey(from, to),
    queryFn: () =>
      apiFetchJson<ScheduleResponse>(
        `/api/v1/servicetitan/schedule?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ),
    enabled: enabled && from !== '' && to !== '',
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });
}

export interface ImportJobResult {
  ok: boolean;
  jobId: number;
  /** True when the job already existed, so the UI can say "already imported" rather than "created". */
  alreadyImported: boolean;
}

/**
 * Import one ServiceTitan job as a QELM job.
 *
 * Safe to press twice: the server returns the SAME job with `alreadyImported` rather than creating
 * another, enforced by a unique index rather than by a check the database cannot make atomic. The
 * button is still disabled while in flight, because a spinner is a better answer than a second
 * request that quietly does nothing.
 *
 * Invalidates the schedule on success so the row flips to its imported state without a manual
 * refresh — the whole window is refetched because one import can only change one row, and a stale
 * board that still offers the button is the confusing outcome.
 */
export function useImportJobMutation() {
  const queryClient = useQueryClient();

  return useMutation<ImportJobResult, ApiError, number>({
    mutationFn: (serviceTitanJobId) =>
      apiFetchJson<ImportJobResult>(`/api/v1/servicetitan/jobs/${serviceTitanJobId}/import`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['servicetitan', 'schedule'] });
    },
  });
}
